import { Component, computed, inject, signal } from '@angular/core';
import { Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { TokenInformation } from '@core/models/auth.model';
import { AuthService } from '@core/services/auth/auth.service';
import { CacheLayerService } from '@core/services/cache-layer/cache-layer.service';
import { ClientsService } from '@core/services/clients/clients.service';
import { NavLoaderService } from '@core/services/nav-loader/nav-loader.service';
import { PermissionsService } from '@core/services/permissions/permissions.service';
import { SessionService } from '@core/services/session/session.service';
import { ClientConfigService } from '@app/layout/service/client-config/client-config.service';
import { jwtDecode } from 'jwt-decode';
import { MessageService } from 'primeng/api';
import { SelectModule } from 'primeng/select';
import { firstValueFrom } from 'rxjs';

interface ClientOption {
  id: number;
  name: string;
}

@Component({
  selector: 'd2d-client-context-selector',
  standalone: true,
  imports: [SelectModule, FormsModule],
  template: `
    @if (visible()) {
      <div class="px-3 pb-3 pt-1">
        @if (clients().length > 1) {
          <p-select
            [options]="clients()"
            [(ngModel)]="selectedClientId"
            optionLabel="name"
            optionValue="id"
            styleClass="w-full !text-sm"
            [disabled]="switching()"
            (onChange)="onClientChange($event.value)"
          />
        } @else {
          <div class="flex items-center gap-2 px-2 py-2 rounded-lg bg-black/10 dark:bg-white/5">
            <i class="pi pi-building text-xs opacity-60"></i>
            <span class="text-xs font-medium truncate opacity-80">{{ clients()[0].name }}</span>
          </div>
        }
      </div>
    }
  `,
})
export class ClientContextSelectorComponent {
  private readonly sessionService = inject(SessionService);
  private readonly authService = inject(AuthService);
  private readonly clientsService = inject(ClientsService);
  private readonly clientConfigService = inject(ClientConfigService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly messageService = inject(MessageService);
  private readonly router = inject(Router);
  private readonly cacheLayerService = inject(CacheLayerService);
  private readonly navLoaderService = inject(NavLoaderService);

  readonly switching = signal(false);
  selectedClientId: number | null = null;

  /** Lista de clientes derivada reactivamente de la sesión activa. */
  readonly clients = computed<ClientOption[]>(() => {
    const session = this.sessionService.session();
    if (!session?.token) return [];

    // Solo visible para usuarios cliente-scope (sin create_client)
    if (this.permissionsService.hasPermission('create_client')) return [];

    // Fuente primaria: internal_clients guardados en la sesión al login (tienen nombres)
    const sessionClients = session.internal_clients ?? [];
    if (sessionClients.length > 0) {
      this.selectedClientId = session.current_client_id ?? null;
      return sessionClients.map((c) => ({ id: c.id, name: c.name }));
    }

    // Fallback: IDs del token (sin nombres, usamos config del cliente activo)
    try {
      const decoded = jwtDecode<TokenInformation>(session.token);
      const ids = decoded.internal_client_ids ?? [];
      if (ids.length === 0) return [];

      this.selectedClientId = session.current_client_id ?? null;

      const config = this.clientConfigService.configSignal();
      return ids.map((id) => ({
        id,
        name:
          config && id === session.current_client_id
            ? (config['name'] ?? `Cliente ${id}`)
            : `Cliente ${id}`,
      }));
    } catch {
      return [];
    }
  });

  /** Muestra el selector solo cuando hay al menos 1 cliente en modo cliente-scope. */
  readonly visible = computed(() => this.clients().length > 0);

  async onClientChange(newClientId: number): Promise<void> {
    if (!newClientId || newClientId === this.sessionService.getActiveSession()?.current_client_id) {
      return;
    }

    this.switching.set(true);
    this.navLoaderService.show('Cambiando de cliente y cargando permisos...');
    try {
      this.cacheLayerService.clear();
      this.permissionsService.clearEffectivePermissions();

      const res = await firstValueFrom(this.authService.switchClient(newClientId));

      this.sessionService.replaceActiveToken(res.access_token, res.current_client_id);

      const clientConfig = await firstValueFrom(
        this.clientsService.getClientWebConfig(res.current_client_id),
      );
      if (clientConfig) {
        this.clientConfigService.setConfig(clientConfig);
      }

      await this.permissionsService.loadEffectivePermissions();

      await this.router.navigate(['/clients/detail', res.current_client_id, 'home']);
    } catch {
      this.messageService.add({
        severity: 'error',
        summary: '¡Error!',
        detail: 'No se pudo cambiar de cliente. Intenta de nuevo.',
        life: 3000,
      });
      this.selectedClientId = this.sessionService.getActiveSession()?.current_client_id ?? null;
    } finally {
      this.switching.set(false);
      this.navLoaderService.hide();
    }
  }
}
